import React, {useState, useEffect} from 'react';
import Pagination from 'react-js-pagination';
import {useSelector} from 'react-redux';
import {NavLink, useHistory, useLocation} from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import {toast} from 'react-toastify';
import {useMutation, useQuery} from 'react-query';
import {DeleteSingle, Get, Post} from '@app/utils/httpProvider';
import {
    ASSET_API,
    ASSETGROUP_API,
    DEPARTMENT_API,
    ASSETSTATUS_API,
    BaseGatewayImage,
    ASSETTRANSFER_API,
    ASSET_ACCESSORY_API,
    MATERIALCATALOG_API,
    MAINTENANCE_EVENT_API
} from '@app/utils/apiUrl.js';
import {URI_INTERNAL} from '../../utils/pathLocations';
import {
    createUrlWithParam,
    formatDateTimeMoment,
    isValidUrl
} from '../../utils/helper';
import {objectShowNumber, messageToast} from '../../utils/define';
import {ConfirmModal} from '../../modules/modal/ConfirmModal';
import Select from 'react-select';

// Hàm tính màu text tương phản dựa trên màu hex nền
const hexToLightBg = (hex) => {
    if (!hex) return {bg: '#f0f0f0', border: '#ccc', text: '#555'};
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return {
        bg: `rgba(${r},${g},${b},0.12)`,
        border: `rgba(${r},${g},${b},0.5)`,
        text: hex
    };
};

const AssetStatusBadge = ({statusName, statusColor}) => {
    let bg, border, text;
    const nameLower = String(statusName || '').toLowerCase();

    if (nameLower === 'đào thải') {
        bg = '#212529'; // Đen
        text = '#ffffff'; // Trắng
        border = '#212529';
    } else if (nameLower === 'chờ phê duyệt' || nameLower.includes('hỏng') || nameLower.includes('sửa')) {
        bg = '#ffc107'; // Vàng
        text = '#000000'; // Chữ đen cho dễ đọc trên nền vàng
        border = '#ffc107';
    } else if (nameLower === 'tài sản mới' || nameLower === 'mới') {
        bg = '#007bff'; // Xanh nước biển
        text = '#ffffff'; // Trắng
        border = '#007bff';
    } else {
        const generated = hexToLightBg(statusColor);
        bg = generated.bg;
        border = generated.border;
        text = generated.text;
    }

    return (
        <span
            style={{
                display: 'inline-block',
                padding: '3px 11px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                background: bg,
                color: text || '#555',
                border: `1px solid ${border}`,
                whiteSpace: 'nowrap',
                letterSpacing: '0.3px'
            }}
        >
            {statusName || '—'}
        </span>
    );
};

const AssetAvatar = ({imagePath}) => {
    const url = imagePath
        ? isValidUrl(imagePath)
            ? imagePath
            : BaseGatewayImage + imagePath
        : null;
    return (
        <div
            style={{
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f4f4f4',
                borderRadius: '4px',
                overflow: 'hidden'
            }}
        >
            {url ? (
                <img
                    src={url}
                    alt="Avatar"
                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
            ) : (
                <i
                    className="feather icon-image text-muted"
                    style={{fontSize: '20px'}}
                />
            )}
        </div>
    );
};

const Asset = () => {
    const history = useHistory();
    const location = useLocation();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');

    const [departmentId, setDepartmentId] = useState('');
    const [assetGroupId, setAssetGroupId] = useState('');
    const [assetStatusId, setAssetStatusId] = useState('');
    const [isDisposed, setIsDisposed] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    // Trigger feather icon replacement after each render
    useEffect(() => {
        if (window.feather) window.feather.replace();
    });

    const handleClone = async (asset) => {
        try {
            // 1. Fetch full asset detail
            const detail = await Get(ASSET_API.GET_ITEM, asset.id);
            if (!detail) {
                toast.error('Không thể tải thông tin tài sản!');
                return;
            }

            // 2. Fetch accessories for this asset
            const accessoryData = await Get(
                `${ASSET_ACCESSORY_API.GET_BY_ASSET_ID}/${asset.id}`
            );
            let clonedAccessories = [];

            if (accessoryData && accessoryData.length > 0) {
                const materialIds = accessoryData.map(
                    (x) => x.materialCatalogId
                );
                const materialRes = await Post(
                    MATERIALCATALOG_API.GET_BY_LIST_ID,
                    null,
                    materialIds
                );
                const materials = await materialRes.json();
                const materialMap = {};
                materials.forEach((m) => {
                    materialMap[m.id] = m;
                });

                clonedAccessories = accessoryData.map((a) => ({
                    materialId: a.materialCatalogId,
                    materialCode:
                        materialMap[a.materialCatalogId]?.materialCode || '',
                    materialName:
                        materialMap[a.materialCatalogId]?.materialName || '',
                    specification:
                        materialMap[a.materialCatalogId]?.specification || '',
                    unit: materialMap[a.materialCatalogId]?.unit || '',
                    quantity: a.quantity,
                    accessoryStatus: a.accessoryStatus || 'Hoạt động',
                    note: a.note || ''
                }));
            }

            // 3. Navigate to AssetAdd with cloned data
            history.push(URI_INTERNAL.ASSET_CREATE_PATH, {
                cloneData: {
                    assetName: detail.assetName,
                    assetGroupId: detail.assetGroupId,
                    currentDepartmentId: detail.currentDepartmentId,
                    serialNumber: detail.serialNumber || '',
                    model: detail.model || '',
                    manufacturer: detail.manufacturer || '',
                    country: detail.country || '',
                    powerSpec: detail.powerSpec || '',
                    purchaseDate: detail.purchaseDate || '',
                    purchaseValue: detail.purchaseValue || 0,
                    usagePurposeId: detail.usagePurposeId || null,
                    manager: detail.manager || '',
                    equipmentTypeId: detail.equipmentTypeId || null,
                    unitOfMeasurement: detail.unitOfMeasurement || '',
                    equipmentSourceId: detail.equipmentSourceId || null,
                    fundingSourceId: detail.fundingSourceId || null,
                    qualityLevelId: detail.qualityLevelId || null,
                    connectionTypeId: detail.connectionTypeId || null,
                    notes: detail.notes || ''
                },
                cloneAccessories: clonedAccessories
            });
        } catch (err) {
            console.error(err);
            toast.error('Không thể sao chép tài sản!');
        }
    };

    const deleteFunc = useMutation((value) =>
        DeleteSingle(ASSET_API.DELETE_ITEM, value, null)
    );

    const paramUrl = new URLSearchParams(location.search.substring(1));
    if (
        paramUrl.get('page') != null &&
        parseInt(paramUrl.get('page'), 10) !== page
    ) {
        const pageResult = parseInt(paramUrl.get('page'), 10);
        setPage(pageResult);
    }
    if (
        paramUrl.get('size') != null &&
        parseInt(paramUrl.get('size'), 10) !== pageSize
    ) {
        const sizeResult = parseInt(paramUrl.get('size'), 10);
        setPageSize(sizeResult);
    }
    if (paramUrl.get('search') != null && paramUrl.get('search') !== search) {
        setSearch(paramUrl.get('search'));
    }

    const {isLoading, isError, error, data, refetch} = useQuery(
        [
            'assetData',
            valueSearch,
            page,
            pageSize,
            departmentId,
            assetGroupId,
            assetStatusId,
            isDisposed
        ],
        () => {
            let model = {
                Keyword: valueSearch ? valueSearch : '',
                PageIndex: page,
                PageSize: pageSize
            };

            if (departmentId)
                model.CurrentDepartmentId = parseInt(departmentId, 10);
            if (assetGroupId) model.AssetGroupId = parseInt(assetGroupId, 10);
            if (assetStatusId)
                model.AssetStatusId = parseInt(assetStatusId, 10);
            if (isDisposed !== '') model.IsDisposed = isDisposed === 'true';

            replaceHistory(page, pageSize, valueSearch ? valueSearch : '');

            return Get(ASSET_API.GET_PAGING, null, model);
        }
    );

    const assetGroups = useQuery(['getAllAssetGroupsForMapping'], () =>
        Get(ASSETGROUP_API.GET_PAGING, null, {PageIndex: 1, PageSize: 10000})
    );

    const departments = useQuery(['getAllDepartmentsForMapping'], () =>
        Get(DEPARTMENT_API.GET_PAGING, null, {PageIndex: 1, PageSize: 10000})
    );

    const assetStatuses = useQuery(['getAllAssetStatusesForMapping'], () =>
        Get(ASSETSTATUS_API.GET_PAGING, null, {PageIndex: 1, PageSize: 10000})
    );

    const getAssetGroupName = (id) => {
        if (assetGroups?.data?.data) {
            const group = assetGroups.data.data.find((x) => x.id === id);
            return group ? group.assetGroupName : id;
        }
        return id;
    };

    const getDepartmentName = (id) => {
        if (departments?.data?.data) {
            const dept = departments.data.data.find((x) => x.id === id);
            return dept ? dept.departmentName : id;
        }
        return id;
    };

    const getAssetStatus = (id) => {
        return assetStatuses?.data?.data?.find((x) => x.id === id) || null;
    };

    const departmentOptions =
        departments?.data?.data?.map((item) => ({
            value: item.id,
            label: item.departmentName
        })) || [];
    const assetGroupOptions =
        assetGroups?.data?.data?.map((item) => ({
            value: item.id,
            label: item.assetGroupName
        })) || [];
    const assetStatusOptions =
        assetStatuses?.data?.data?.map((item) => ({
            value: item.id,
            label: item.statusName
        })) || [];
    const isDisposedOptions = [
        {value: 'true', label: 'Đã thanh lý'},
        {value: 'false', label: 'Chưa thanh lý'}
    ];

    const customSelectStyles = {
        container: (provided) => ({...provided, width: '100%'}),
        valueContainer: (provided) => ({...provided, flexWrap: 'nowrap'}),
        placeholder: (provided) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        })
    };

    useEffect(() => {
        refetch();
    }, [pageSize, page, departmentId, assetGroupId, assetStatusId, isDisposed]);

    const replaceHistory = async (Page, Size, Search) => {
        history.replace(
            createUrlWithParam(URI_INTERNAL.ASSET_PATH, {
                page: Page,
                size: Size,
                search: Search
            })
        );
    };

    React.useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            replaceHistory(1, pageSize, valueSearch);
        }
    }, [valueSearch]);

    const changePage = (number) => {
        setPage(number);
        replaceHistory(number, pageSize, search);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(e.target.value);
        replaceHistory(page, e.target.value, search);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (idWantDelete !== '') {
            deleteFunc.mutate(idWantDelete, {
                onSuccess: (res) => {
                    if (
                        res.ok ||
                        res.status === 200 ||
                        res.status === 201 ||
                        res.status === 204
                    ) {
                        refetch();
                        toast.success(messageToast.messageToastDeleteSuccess);
                        if (
                            data != null &&
                            data.data.length === 1 &&
                            page !== 1
                        ) {
                            history.push(
                                createUrlWithParam(URI_INTERNAL.ASSET_PATH, {
                                    page: page - 1,
                                    size: pageSize,
                                    search: search
                                })
                            );
                        }
                    } else {
                        toast.error(messageToast.messageToastDeleteError);
                    }
                },
                onError: (res) => {
                    toast.error(messageToast.messageToastTryCatch);
                }
            });

            replaceHistory(page, pageSize, search);
            setOpenConfirmDeleteModal(false);
        }
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const goToTransferPage = (item) => {
        history.push({
            pathname: URI_INTERNAL.ASSET_TRANSFER_PATH,
            state: {
                asset: item,
                departmentName: getDepartmentName(item.currentDepartmentId)
            }
        });
    };

    const handleTransferClick = async (item) => {
        const statusObj = getAssetStatus(item.assetStatusId);
        if (item?.isDisposed || statusObj?.statusName === 'Đào thải') {
            toast.error('Tài sản này đã bị đào thải, không thể điều chuyển!');
            return;
        }

        if (statusObj?.statusName === 'Đang điều chuyển') {
            toast.warning('Tài sản này đang trong quá trình điều chuyển!');
            return;
        }

        try {
            const resTranfer = await Get(
                `${ASSETTRANSFER_API.GET_BY_ASSET_ID}/${item.id}`
            );
            const listTransfer = resTranfer?.data || resTranfer || [];
            const isTransferring = listTransfer.some(
                (transfer) =>
                    transfer.status === 'Chờ phê duyệt' ||
                    transfer.status === 'Đã phê duyệt'
            );

            if (isTransferring) {
                toast.warning('Tài sản này đang trong quá trình điều chuyển!');
                return;
            }

            const resMaintenance = await Get(
                `${MAINTENANCE_EVENT_API.GET_BY_ASSET_ID}/${item.id}`
            );
            const listMaintenance =
                resMaintenance?.data || resMaintenance || [];
            const isMaintaining = listMaintenance.some(
                (m) =>
                    m.status === 'Chờ phê duyệt' || m.status === 'Đã phê duyệt'
            );

            if (isMaintaining) {
                toast.warning('Tài sản này đang trong quá trình sửa chữa');
                return;
            }

            goToTransferPage(item);
        } catch (error) {
            goToTransferPage(item);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white py-3">
                            <h5 className="card-title fw-bold text-primary mb-0">
                                <i className="fas fa-boxes mr-2" />
                                Danh sách tài sản
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3 align-items-center">
                                {/* Action Buttons - Top on Mobile */}
                                <div className="col-12 col-md-auto order-1 order-md-2 ml-md-auto mb-3 mb-md-0">
                                    <div className="d-flex justify-content-center justify-content-md-end align-items-center">
                                        <NavLink
                                            to={
                                                URI_INTERNAL.ASSET_BULK_IMPORT_PATH
                                            }
                                            title="Import file Excel"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm shadow-sm btn-action-equal d-inline-flex align-items-center justify-content-center"
                                            >
                                                <i className="fas fa-file-excel mr-1" />{' '}
                                                Import Excel
                                            </button>
                                        </NavLink>
                                        <NavLink
                                            to={URI_INTERNAL.ASSET_CREATE_PATH}
                                            className="ml-2"
                                            title="Thêm tài sản mới"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-info btn-sm shadow-sm btn-action-equal d-inline-flex align-items-center justify-content-center"
                                            >
                                                <i className="fas fa-plus mr-1" />{' '}
                                                Thêm mới
                                            </button>
                                        </NavLink>
                                    </div>
                                </div>

                                {/* Filter & Page Size - Bottom on Mobile */}
                                <div className="col-12 col-md-auto order-2 order-md-1 d-flex align-items-center justify-content-between justify-content-md-start">
                                    <div className="d-flex align-items-center mr-3">
                                        <label
                                            htmlFor="showNumber"
                                            className="mr-2 mb-0 text-nowrap"
                                        >
                                            Hiển thị:
                                        </label>
                                        <select
                                            id="showNumber"
                                            className="form-control form-control-sm"
                                            value={pageSize}
                                            onChange={(e) =>
                                                onChangeShowNumber(e)
                                            }
                                            style={{width: 'auto'}}
                                        >
                                            {objectShowNumber.map(
                                                (item, key) => (
                                                    <option
                                                        key={Number(key)}
                                                        value={item.value}
                                                    >
                                                        {item.text}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>

                                    <button
                                        className={`btn btn-sm d-flex align-items-center justify-content-center btn-action-equal ${
                                            showFilters
                                                ? 'btn-primary shadow-sm'
                                                : 'btn-outline-primary'
                                        }`}
                                        onClick={() =>
                                            setShowFilters(!showFilters)
                                        }
                                    >
                                        <i className="fas fa-filter mr-2" />
                                        <span>Bộ lọc</span>
                                        {showFilters ? (
                                            <i className="fas fa-chevron-up ml-2" />
                                        ) : (
                                            <i className="fas fa-chevron-down ml-2" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {showFilters && (
                                <div className="row mb-3 p-3 bg-light rounded mx-0 shadow-sm animate__animated animate__fadeIn">
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="small text-muted mb-1">
                                            Phòng ban
                                        </div>
                                        <Select
                                            options={departmentOptions}
                                            value={
                                                departmentOptions.find(
                                                    (opt) =>
                                                        opt.value ===
                                                        departmentId
                                                ) || null
                                            }
                                            onChange={(selectedOption) =>
                                                setDepartmentId(
                                                    selectedOption
                                                        ? selectedOption.value
                                                        : ''
                                                )
                                            }
                                            placeholder="-- Tất cả phòng ban --"
                                            isClearable={true}
                                            styles={customSelectStyles}
                                        />
                                    </div>

                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="small text-muted mb-1">
                                            Nhóm tài sản
                                        </div>
                                        <Select
                                            options={assetGroupOptions}
                                            value={
                                                assetGroupOptions.find(
                                                    (opt) =>
                                                        opt.value ===
                                                        assetGroupId
                                                ) || null
                                            }
                                            onChange={(selectedOption) =>
                                                setAssetGroupId(
                                                    selectedOption
                                                        ? selectedOption.value
                                                        : ''
                                                )
                                            }
                                            placeholder="-- Tất cả nhóm TS --"
                                            isClearable={true}
                                            styles={customSelectStyles}
                                        />
                                    </div>

                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="small text-muted mb-1">
                                            Trạng thái
                                        </div>
                                        <Select
                                            options={assetStatusOptions}
                                            value={
                                                assetStatusOptions.find(
                                                    (opt) =>
                                                        opt.value ===
                                                        assetStatusId
                                                ) || null
                                            }
                                            onChange={(selectedOption) =>
                                                setAssetStatusId(
                                                    selectedOption
                                                        ? selectedOption.value
                                                        : ''
                                                )
                                            }
                                            placeholder="-- Tất cả trạng thái --"
                                            isClearable={true}
                                            styles={customSelectStyles}
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <div className="small text-muted mb-1">
                                            Tình trạng thanh lý
                                        </div>
                                        <Select
                                            options={isDisposedOptions}
                                            value={
                                                isDisposedOptions.find(
                                                    (opt) =>
                                                        opt.value === isDisposed
                                                ) || null
                                            }
                                            onChange={(selectedOption) =>
                                                setIsDisposed(
                                                    selectedOption
                                                        ? selectedOption.value
                                                        : ''
                                                )
                                            }
                                            placeholder="-- Thanh lý --"
                                            isClearable={true}
                                            styles={customSelectStyles}
                                        />
                                    </div>
                                </div>
                            )}

                            {isLoading ? (
                                <div className="text-center p-5">
                                    Đang tải...
                                </div>
                            ) : isError ? (
                                <div className="alert alert-danger">
                                    Có lỗi xảy ra: {error?.message}
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive border-0">
                                        <Table
                                            striped
                                            bordered
                                            hover
                                            size="sm"
                                            className="mb-0 mobile-card-view"
                                        >
                                            <thead>
                                                <tr
                                                    className="text-dark fw-bold text-uppercase"
                                                    style={{fontSize: '12px'}}
                                                >
                                                    <th
                                                        className="text-center py-2"
                                                        style={{width: '50px'}}
                                                    >
                                                        STT
                                                    </th>
                                                    <th
                                                        className="py-2"
                                                        style={{width: '60px'}}
                                                    >
                                                        Ảnh
                                                    </th>
                                                    <th className="py-2">
                                                        Mã tài sản
                                                    </th>
                                                    <th className="py-2">
                                                        Tên tài sản
                                                    </th>
                                                    <th
                                                        className="text-center py-2"
                                                        style={{width: '130px'}}
                                                    >
                                                        Ngày tạo
                                                    </th>
                                                    <th className="py-2">
                                                        Nhóm TS
                                                    </th>
                                                    <th className="py-2">
                                                        Phòng ban
                                                    </th>
                                                    <th className="text-center py-2">
                                                        Trạng thái
                                                    </th>
                                                    <th
                                                        className="text-center py-2"
                                                        style={{width: '60px'}}
                                                    >
                                                        Đã TL
                                                    </th>
                                                    <th
                                                        className="text-center py-2"
                                                        style={{
                                                            width: '150px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        Chức năng
                                                    </th>
                                                </tr>
                                            </thead>
                                            {data?.data?.length > 0 ? (
                                                <tbody>
                                                    {data.data.map(
                                                        (item, index) => {
                                                            const statusObj =
                                                                getAssetStatus(
                                                                    item.assetStatusId
                                                                );
                                                            const statusColor =
                                                                statusObj?.statusColor ||
                                                                null;
                                                            return (
                                                                <tr
                                                                    key={
                                                                        item.id
                                                                    }
                                                                >
                                                                    <td
                                                                        className="text-center align-middle py-2"
                                                                        data-label="STT"
                                                                    >
                                                                        {index +
                                                                            1 +
                                                                            (page -
                                                                                1) *
                                                                                pageSize}
                                                                    </td>
                                                                    <td
                                                                        className="align-middle py-2"
                                                                        data-label="ẢNH"
                                                                    >
                                                                        <AssetAvatar
                                                                            imagePath={
                                                                                item.imageThumbnail ||
                                                                                item.imagePath
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className="align-middle py-2 fw-bold"
                                                                        data-label="MÃ TÀI SẢN"
                                                                    >
                                                                        {
                                                                            item.assetCode
                                                                        }
                                                                    </td>
                                                                    <td
                                                                        className="align-middle py-2"
                                                                        data-label="TÊN TÀI SẢN"
                                                                    >
                                                                        <div>
                                                                            {
                                                                                item.assetName
                                                                            }
                                                                        </div>
                                                                        {item.serialNumber && (
                                                                            <div
                                                                                style={{
                                                                                    fontSize:
                                                                                        '0.75rem',
                                                                                    color: '#888',
                                                                                    marginTop: 2
                                                                                }}
                                                                            >
                                                                                #{' '}
                                                                                {
                                                                                    item.serialNumber
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        className="text-center align-middle py-2 small"
                                                                        data-label="NGÀY TẠO"
                                                                    >
                                                                        {item.createdDate
                                                                            ? formatDateTimeMoment(
                                                                                  item.createdDate
                                                                              )
                                                                            : ''}
                                                                    </td>
                                                                    <td
                                                                        className="align-middle py-2 small"
                                                                        data-label="NHÓM TS"
                                                                    >
                                                                        {getAssetGroupName(
                                                                            item.assetGroupId
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        className="align-middle py-2 small"
                                                                        data-label="PHÒNG BAN"
                                                                    >
                                                                        {getDepartmentName(
                                                                            item.currentDepartmentId
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        className="text-center align-middle py-2"
                                                                        data-label="TRẠNG THÁI"
                                                                    >
                                                                        <AssetStatusBadge
                                                                            statusName={
                                                                                statusObj?.statusName ||
                                                                                item.assetStatusId
                                                                            }
                                                                            statusColor={
                                                                                statusColor
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className="text-center align-middle py-2 small"
                                                                        data-label="ĐÃ TL"
                                                                    >
                                                                        {item.isDisposed
                                                                            ? 'Có'
                                                                            : 'Không'}
                                                                    </td>
                                                                    <td className="text-center align-middle py-3">
                                                                        <div
                                                                            className="d-flex justify-content-center align-items-center"
                                                                            style={{
                                                                                gap: '4px'
                                                                            }}
                                                                        >
                                                                            {/* Chi tiết */}
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-xs btn-outline-primary"
                                                                                title="Chi tiết"
                                                                                style={{
                                                                                    width: '28px',
                                                                                    height: '28px',
                                                                                    padding: 0,
                                                                                    display:
                                                                                        'inline-flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'center'
                                                                                }}
                                                                                onClick={() =>
                                                                                    history.push(
                                                                                        createUrlWithParam(
                                                                                            URI_INTERNAL.ASSET_EDIT_PATH,
                                                                                            {
                                                                                                id: item.id
                                                                                            }
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i
                                                                                    className="fas fa-eye"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            '13px'
                                                                                    }}
                                                                                />
                                                                            </button>
                                                                            {/* Điều chuyển */}
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-xs btn-outline-warning"
                                                                                title="Điều chuyển"
                                                                                style={{
                                                                                    width: '28px',
                                                                                    height: '28px',
                                                                                    padding: 0,
                                                                                    display:
                                                                                        'inline-flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'center'
                                                                                }}
                                                                                onClick={() =>
                                                                                    handleTransferClick(
                                                                                        item
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i
                                                                                    className="fas fa-exchange-alt"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            '13px'
                                                                                    }}
                                                                                />
                                                                            </button>
                                                                            {/* Sao chép */}
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-xs btn-outline-info"
                                                                                title="Sao chép"
                                                                                style={{
                                                                                    width: '28px',
                                                                                    height: '28px',
                                                                                    padding: 0,
                                                                                    display:
                                                                                        'inline-flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'center'
                                                                                }}
                                                                                onClick={() =>
                                                                                    handleClone(
                                                                                        item
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i
                                                                                    className="fas fa-copy"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            '13px'
                                                                                    }}
                                                                                />
                                                                            </button>
                                                                            {/* Xóa */}
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-xs btn-outline-danger"
                                                                                title="Xóa"
                                                                                style={{
                                                                                    width: '28px',
                                                                                    height: '28px',
                                                                                    padding: 0,
                                                                                    display:
                                                                                        'inline-flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    justifyContent:
                                                                                        'center'
                                                                                }}
                                                                                onClick={() =>
                                                                                    deleteAction(
                                                                                        item.id,
                                                                                        item.assetName
                                                                                    )
                                                                                }
                                                                            >
                                                                                <i
                                                                                    className="fas fa-trash-alt"
                                                                                    style={{
                                                                                        fontSize:
                                                                                            '13px'
                                                                                    }}
                                                                                />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            colSpan="10"
                                                            className="text-center p-4"
                                                        >
                                                            Không có dữ liệu
                                                            hiển thị
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )}
                                        </Table>
                                    </div>

                                    <div className="d-flex justify-content-center mt-3">
                                        <Pagination
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            innerClass="pagination pagination-sm"
                                            activePage={page}
                                            itemsCountPerPage={Number(pageSize)}
                                            totalItemsCount={
                                                data?.totalRecords || 0
                                            }
                                            pageRangeDisplayed={5}
                                            onChange={changePage}
                                        />
                                    </div>
                                    <ConfirmModal
                                        isOpen={openConfirmDeleteModal}
                                        confirmType="delete"
                                        handleAccept={() => acceptDelete()}
                                        handleCancel={() => cancelDelete()}
                                        content={confirmDelMessage}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            
        </div>
    );
};

export default Asset;
